import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Model from "@tripian/model";
import TripianLogo from "../img/tripian-bg.png";
import { useSelector } from "react-redux";
import ICombinedState from "../../../redux/model/ICombinedState";
import { TRIPS } from "../../../constants/ROUTER_PATH_TITLE";

import "./style.scss";

type Props = {
  theme?: "dark" | "light";
  t: (value: Model.TranslationKey) => string;
};

const Navbar: React.FC<Props> = ({
  theme = "light",
  t,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const hamburgerButtonRef = useRef<HTMLButtonElement>(null);

  // Determine active page
  const isHomePage = location.pathname === '/' || location.pathname === '/home';
  const isDiscoverPage = location.pathname.startsWith('/discover');
  const isTripPlannerPage = location.pathname.startsWith('/trip-planner');

    const isLoggedIn = useSelector((state: ICombinedState) => {
        const userEmail = state.user.user?.email || "";
        if (userEmail.includes("tripianguest.com")) {
            return false;
        }
        return state.user.isLoggedIn;
    });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node) &&
        hamburgerButtonRef.current &&
        !hamburgerButtonRef.current.contains(event.target as Node)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  return (
    <nav className="absolute z-20 w-full md:px-[100px] md:pt-[56px]">
      <div
        className="bg-white backdrop-blur-sm shadow-lg flex items-center justify-between border md:rounded-[56px] p-4 md:px-6 md:py-4"
      >
        {/* Left side - Logo and Navigation */}
        <div className="flex items-center space-x-4 md:space-x-8">
          <img
            src={
              theme === "dark"
                ? window.tconfig.IMAGE_PATHS.LOGO_PATH_DARK
                : window.tconfig.IMAGE_PATHS.LOGO_PATH_LIGHT || TripianLogo
            }
            alt=""
            className="max-h-10 md:max-h-14 max-w-[8rem] md:max-w-[10rem] w-auto h-auto p-1 md:p-2 cursor-pointer"
            onClick={() => navigate('/')}
          />

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-6">
            <a
              href="/"
              className={`font-15-h font-semibold hover:text-gray-600 transition-colors ${
                isHomePage ? 'text-black' : 'text-gray-500'
              }`}
            >
                {t('valai.home')}
            </a>
            <a
              href="/discover"
              className={`font-15-h font-semibold hover:text-gray-600 transition-colors ${
                isDiscoverPage ? 'text-black' : 'text-gray-500'
              }`}
            >
                {t('valai.discover')}
            </a>
            <a
              href="/trip-planner"
              className={`font-15-h font-semibold hover:text-gray-600 transition-colors ${
                isTripPlannerPage ? 'text-black' : 'text-gray-500'
              }`}
            >
                {t('valai.tripPlanner')}
            </a>
          </div>
        </div>
        
        {/* Right side - Language and Sign In */}
        <div className="flex items-center space-x-2 md:space-x-4">
          {/* Mobile Hamburger Menu Button */}
          <button
            ref={hamburgerButtonRef}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Menu"
          >
            {isMobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>

          {/* Desktop - Profile Icon or Log in / Sign up Buttons */}
          {isLoggedIn ? (
            <button
              onClick={() => navigate(TRIPS.PATH)}
              className="hidden md:flex items-center justify-center w-10 h-10 bg-[#DBEAFE] rounded-full hover:bg-blue-200 transition-colors"
              aria-label="Profile"
            >
              <svg className="w-5 h-5 text-[#2563EB]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </button>
          ) : (
            <div className="hidden md:flex items-center space-x-3">
              <button
                onClick={() => navigate('/login')}
                className="text-gray-700 font-medium px-4 py-2 hover:text-gray-900 transition-colors"
              >
                {t('valai.login')}
              </button>
              <button
                onClick={() => navigate('/register')}
                className="bg-[#2563EB] text-white font-medium px-5 py-2 rounded-full hover:bg-blue-700 transition-colors"
              >
                {t('valai.signup')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          ref={mobileMenuRef}
          className="md:hidden absolute top-full left-0 right-0 mt-2 bg-white shadow-xl border-t border-gray-100 overflow-hidden z-50"
        >
          {/* Navigation Links */}
          <div className="py-2">
            <a
              href="/"
              className={`block px-6 py-3 font-semibold hover:bg-gray-50 transition-colors ${
                isHomePage ? 'text-black bg-gray-50' : 'text-gray-700'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
                {t('valai.home')}
            </a>
            <a
              href="/discover"
              className={`block px-6 py-3 font-semibold hover:bg-gray-50 transition-colors ${
                isDiscoverPage ? 'text-black bg-gray-50' : 'text-gray-700'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
                {t('valai.discover')}
            </a>
            <a
              href="/trip-planner"
              className={`block px-6 py-3 font-semibold hover:bg-gray-50 transition-colors ${
                isTripPlannerPage ? 'text-black bg-gray-50' : 'text-gray-700'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
                {t('valai.tripPlanner')}
            </a>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200"></div>

          {/* Profile or Log in / Sign up Buttons */}
          <div className="p-4 space-y-3">
            {isLoggedIn ? (
              <button
                onClick={() => {
                  navigate(TRIPS.PATH);
                  setIsMobileMenuOpen(false);
                }}
                className="w-full flex items-center justify-center gap-2 bg-[#DBEAFE] text-[#2563EB] font-medium py-3 rounded-full hover:bg-blue-200 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {t('landing.planYourTrip')}
              </button>
            ) : (
              <>
                <button
                  onClick={() => {
                    navigate('/login');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full text-gray-700 font-medium py-3 hover:text-gray-900 transition-colors"
                >
                  {t('valai.login')}
                </button>
                <button
                  onClick={() => {
                    navigate('/register');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full bg-[#2563EB] text-white font-medium py-3 rounded-full hover:bg-blue-700 transition-colors"
                >
                  {t('valai.signup')}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
